import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, FileText, Download, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendorContractPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/contracts/public/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Contract not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSign = async () => {
    if (!signerName.trim() || !signatureText.trim()) {
      toast.error('Please type your name and signature');
      return;
    }
    if (!accepted) {
      toast.error('You must accept the contract terms');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/contracts/public/${token}/sign`, {
        signer_name: signerName,
        signature_text: signatureText,
        accepted_terms: accepted,
      });
      toast.success('Contract signed.');
      setData({ ...data, is_signed: true, signed_at: r.data.signed_at, html: r.data.html, signer_name: signerName, signature_text: signatureText });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadUrl = `${API}/contracts/public/${token}/download`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>;
  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-bold">Contract unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="vendor-contract-public">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            <h1 className="text-xl font-bold">Vendor Service Agreement</h1>
          </div>
          <a href={downloadUrl} className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:underline" data-testid="contract-download">
            <Download className="w-4 h-4" /> Download
          </a>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <iframe
              title="Vendor Contract"
              srcDoc={data?.html}
              className="w-full"
              style={{ minHeight: 720, border: 0, background: 'white' }}
              data-testid="contract-iframe"
            />
          </CardContent>
        </Card>

        {data?.is_signed ? (
          <Card className="mt-6 border-emerald-300 bg-emerald-50">
            <CardContent className="p-5 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Signed on {new Date(data.signed_at).toLocaleString()}</div>
                <div className="text-sm text-emerald-800">By {data.signer_name}. A signed copy is available via the Download button above.</div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mt-6">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-semibold text-lg">Sign here</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Full legal name</label>
                  <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Jane Doe" data-testid="signer-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Typed signature</label>
                  <Input
                    value={signatureText}
                    onChange={(e) => setSignatureText(e.target.value)}
                    placeholder="/s/ Jane Doe"
                    style={{ fontFamily: 'Brush Script MT, cursive', fontSize: '20px' }}
                    data-testid="signer-signature-input"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="accept-contract-checkbox" />
                <span className="text-sm">I have read and agree to the terms of this Vendor Service Agreement and authorize my typed signature as a legally binding signature.</span>
              </label>
              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600"
                disabled={submitting}
                onClick={onSign}
                data-testid="sign-contract-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Sign &amp; submit
              </Button>
              <p className="text-[11px] text-center text-muted-foreground">After signing you'll be able to download a signed copy.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
